import React, { Suspense, useEffect } from "react";
import { Box, Flex } from "@chakra-ui/react";
import PageHeader from "@/shared/components/PageHeader";
import OrderHistoryTitleSection from "@/features/my-order/features/order-history/components/title-section";
import OrderHistoryOrderInfoSection from "@/features/my-order/features/order-history/components/order-info-section";
import OrderHistoryDocumentSection from "@/features/my-order/features/order-history/components/document-section";
import OrderHistoryProgressSection from "@/features/my-order/features/order-history/components/progress-section";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import CenteredSpinner from "@/shared/components/centered-spinner";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import OrderHistoryInvoiceSection from "@/features/my-order/features/order-history/components/invoice-section";
import OrderHistoryTransportationSection from "@/features/my-order/features/order-history/components/transportation-section";
import OrderHistoryInquirySection from "@/features/my-order/features/order-history/components/inquiry-section";
import useOrderHistoryProformaInvoice from "@/features/my-order/features/order-history/hooks/useOrderHistoryProformaInvoice";
import OrderHistoryClaimSection from "@/features/my-order/features/order-history/components/claim-section";
import useGetClaimList from "@/features/my-order/features/order-history/features/claim/hooks/useGetClaimList";
import { OrderHistoryClaimModal } from "@/features/my-order/features/order-history/features/claim";
import { useClaimModalStore } from "@/features/my-order/features/order-history/stores/useClaimModalStore";

function MyOrderOrderHistory() {
  const { navigate } = useRouterHelper();
  const { tTitle } = useI18nHelper();
  const { data, proformaInvoices } = useOrderHistoryProformaInvoice();
  const { data: claimList } = useGetClaimList();

  return (
    <Flex flexDirection={"column"} height={"100%"}>
      <PageHeader
        iconName={"chevron-left"}
        title={tTitle("order_history:order_detail")}
        onIconClick={() =>
          // defaultPage를 주지 않아야 회사 타입·모바일 여부에 맞는 기본 화면으로 간다.
          navigate(-1, { replace: true })
        }
      />
      <Flex
        flexDirection={"column"}
        gap={"16px"}
        padding={"16px"}
        alignItems={"center"}
        overflow={"auto"}
        style={{
          scrollbarGutter: "stable",
        }}
        flex={1}
      >
        <Flex
          gap={"16px"}
          alignItems={"flex-start"}
          flexDirection={{ base: "column", lg: "row" }}
          width={{ base: "100%", lg: "1176px" }}
          maxWidth={{ base: "800px", lg: "1176px" }}
          flex={1}
        >
          {/* 메인 정보 컬럼 */}
          <Flex
            flexDirection={"column"}
            gap={"16px"}
            flex={{ base: "none", lg: "1" }}
            width={{ base: "100%", lg: "auto" }}
            minW={"800px"}
          >
            <Suspense fallback={<CenteredSpinner />}>
              <OrderHistoryTitleSection />
              <OrderHistoryProgressSection />
              <OrderHistoryOrderInfoSection />
              {/* 프로포르마가 없어도 공개된 OC(디파짓)가 있으면 송금 정보를 보여준다 */}
              {(proformaInvoices?.length !== 0 ||
                (data?.order_confirmation_invoices ?? []).some((oc) => !!oc.name)) && (
                <OrderHistoryInvoiceSection />
              )}
              {data?.transportations.length !== 0 && (
                <OrderHistoryTransportationSection />
              )}
              {claimList && claimList.claimProducts.length > 0 && (
                <OrderHistoryClaimSection />
              )}
              <OrderHistoryDocumentSection />
            </Suspense>
          </Flex>

          {/* 문의/답변 사이드 패널 (우측 고정, lg 미만은 하단으로 스택) */}
          <Box
            width={{ base: "100%", lg: "360px" }}
            flexShrink={0}
            position={{ base: "static", lg: "sticky" }}
            top={"0"}
            alignSelf={"flex-start"}
          >
            <Suspense fallback={<CenteredSpinner />}>
              <OrderHistoryInquirySection />
            </Suspense>
          </Box>
        </Flex>
      </Flex>
      <OrderHistoryClaimModalWrapper />
    </Flex>
  );
}

export default MyOrderOrderHistory;

const OrderHistoryClaimModalWrapper = () => {
  const { isOpen } = useClaimModalStore();

  return (
    <>
      {isOpen && (
        <Suspense fallback={null}>
          <OrderHistoryClaimModal />
        </Suspense>
      )}
    </>
  );
};
